import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { privacyService } from '@/services/privacy-service'
import {
  PrivacySubject,
  ListPrivacyParams,
  UpsertPrivacyRequest,
  UpdatePrivacyRequest,
} from '@/models/privacy'

// Query keys
export const privacyKeys = {
  all: ['privacy'] as const,
  lists: () => [...privacyKeys.all, 'list'] as const,
  list: (params?: ListPrivacyParams) => [...privacyKeys.lists(), params] as const,
  details: () => [...privacyKeys.all, 'detail'] as const,
  detail: (id: string) => [...privacyKeys.details(), id] as const,
  stats: () => [...privacyKeys.all, 'stats'] as const,
  audits: () => [...privacyKeys.all, 'audit'] as const,
  audit: (id: string) => [...privacyKeys.audits(), id] as const,
}

/**
 * Hook to fetch paginated list of privacy subjects
 */
export function usePrivacyList(params?: ListPrivacyParams) {
  return useQuery({
    queryKey: privacyKeys.list(params),
    queryFn: () => privacyService.list(params),
  })
}

/**
 * Hook to fetch ALL privacy subjects (for client-side pagination/search)
 * Fetches in batches of 1000 until all records are loaded
 */
export function usePrivacyListAll() {
  return useQuery({
    queryKey: [...privacyKeys.lists(), 'all'] as const,
    queryFn: async () => {
      const allSubjects: PrivacySubject[] = []
      let page = 1
      const pageSize = 1000

      while (true) {
        const response = await privacyService.list({ page, page_size: pageSize })
        const subjects = response.subjects ?? []
        allSubjects.push(...subjects)

        // Stop if we've loaded all records or got fewer than requested
        if (allSubjects.length >= (response.total ?? 0) || subjects.length < pageSize) {
          break
        }
        page++
      }

      return { subjects: allSubjects, total: allSubjects.length }
    },
  })
}

/**
 * Hook to fetch a single privacy subject by ID
 */
export function usePrivacyDetail(id: string) {
  return useQuery({
    queryKey: privacyKeys.detail(id),
    queryFn: () => privacyService.get(id),
    enabled: !!id,
  })
}

/**
 * Hook to fetch privacy statistics
 */
export function usePrivacyStats() {
  return useQuery({
    queryKey: privacyKeys.stats(),
    queryFn: () => privacyService.getStats(),
  })
}

/**
 * Hook to fetch audit log for a privacy subject
 */
export function usePrivacyAudit(id: string | null) {
  return useQuery({
    queryKey: privacyKeys.audit(id ?? ''),
    queryFn: () => privacyService.getAudit(id!),
    enabled: !!id,
  })
}

/**
 * Hook to create/upsert a privacy subject
 */
export function useCreatePrivacy() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: UpsertPrivacyRequest) => privacyService.upsert(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: privacyKeys.lists() })
      queryClient.invalidateQueries({ queryKey: privacyKeys.stats() })
    },
  })
}

/**
 * Hook to update a privacy subject
 */
export function useUpdatePrivacy() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: UpdatePrivacyRequest }) =>
      privacyService.update(id, request),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: privacyKeys.lists() })
      queryClient.invalidateQueries({ queryKey: privacyKeys.detail(variables.id) })
      queryClient.invalidateQueries({ queryKey: privacyKeys.stats() })
      queryClient.invalidateQueries({ queryKey: privacyKeys.audit(variables.id) })
    },
  })
}

/**
 * Hook to delete a privacy subject
 */
export function useDeletePrivacy() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => privacyService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: privacyKeys.lists() })
      queryClient.invalidateQueries({ queryKey: privacyKeys.stats() })
    },
  })
}

/**
 * Hook to import privacy subjects from CSV
 */
export function useImportPrivacy() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (file: File) => privacyService.import(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: privacyKeys.lists() })
      queryClient.invalidateQueries({ queryKey: privacyKeys.stats() })
    },
  })
}
